import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useAuth from '../hooks/utils/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { FiActionPayload, FiMessageRow, fiKeys, postFiChat, postFiReset, useFiHistory } from '../hooks/api/useFiChat';
import { useSessionStorage } from '../hooks/utils/useSessionStorage';
import type { FiStarterAction, FiStarterPathway } from '../components/fi/fi-starter-actions';
import { FI_SEARCH_PROGRESS_EVENT, FiSearchProgressPayload } from '../hooks/api/useNotifications';
import {
  CrisisCheckResponse,
  fetchCrisisCheck,
  isCrisisCheckInCooldown,
  setCrisisCheckCooldown,
} from '../hooks/api/useCrisisCheck';
import type { Resource } from '../hooks/api/useResources';
import { Features, useFeatureFlag } from '../hooks/api/useFeatureFlag';
import { useAssistantWidget } from './assistant-widget-context';

/**
 * Fi is visible by default. The feature flag only hides it when the flag row
 * exists in the DB and is explicitly set to `enabled: false`.
 */
function useFiFeatureEnabled(): boolean {
  const { data, enabled } = useFeatureFlag(Features.FI_CONCIERGE);
  const flagExists = data?.data?.[Features.FI_CONCIERGE] != null;
  if (!flagExists) return true;
  return enabled;
}

const INTRO_KEY = 'fi_intro_dismissed';

/** Fresh-login flag from `auth-context`; consuming it ends the active Fi thread server-side and renders blank. */
const FI_FRESH_SESSION_KEY = 'kh:fi:freshSession';
const consumeFiFreshSessionFlag = (): boolean => {
  try {
    if (sessionStorage.getItem(FI_FRESH_SESSION_KEY) !== '1') return false;
    sessionStorage.removeItem(FI_FRESH_SESSION_KEY);
    return true;
  } catch {
    return false;
  }
};

export type FiCrisisPayload = {
  emergency: boolean;
  selfharm: boolean;
  crisisResource?: Resource;
};

function findLastUserMessageIndex(messages: FiDisplayMessage[], userContent: string): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role === 'user' && m.content === userContent) return i;
  }
  return -1;
}

/**
 * Insert crisis row immediately after the last user turn matching `userContent`
 * and suppress any subsequent assistant message (content + tool_results cleared).
 */
function insertCrisisAfterUser(
  messages: FiDisplayMessage[],
  userContent: string,
  crisis: Pick<CrisisCheckResponse, 'emergency' | 'selfharm' | 'crisis_resource'>,
): FiDisplayMessage[] {
  const idx = findLastUserMessageIndex(messages, userContent);
  if (idx === -1) return messages;
  if (messages[idx + 1]?.fi_kind === 'crisis') return messages;
  const crisisRow: FiDisplayMessage = {
    role: 'assistant',
    content: '',
    fi_kind: 'crisis',
    crisis: {
      emergency: crisis.emergency,
      selfharm: crisis.selfharm,
      crisisResource: crisis.crisis_resource ?? undefined,
    },
    timestamp: new Date().toISOString(),
  };
  const result = [...messages.slice(0, idx + 1), crisisRow];
  for (let i = idx + 1; i < messages.length; i++) {
    const m = messages[i];
    if (m.role === 'assistant' && m.fi_kind !== 'crisis') {
      result.push({ ...m, content: '', tool_results: undefined });
    } else {
      result.push(m);
    }
  }
  return result;
}

function extractCrisisRowAfterUser(messages: FiDisplayMessage[], userContent: string): FiDisplayMessage | null {
  const idx = findLastUserMessageIndex(messages, userContent);
  if (idx === -1) return null;
  const next = messages[idx + 1];
  if (next?.fi_kind === 'crisis' && next.crisis) return next;
  return null;
}

/**
 * After Fi returns server history, keep an inline crisis row (if any) between
 * that user turn and assistant. When crisis is present, suppress the assistant
 * response that follows (clear content + tool_results).
 */
function mergeFiHistoryPreservingCrisis(
  prev: FiDisplayMessage[],
  serverHistory: FiDisplayMessage[],
  userContent: string,
  crisisFromCheck: CrisisCheckResponse | null,
): FiDisplayMessage[] {
  const uidx = findLastUserMessageIndex(serverHistory, userContent);
  if (uidx === -1) return serverHistory;
  if (serverHistory[uidx + 1]?.fi_kind === 'crisis') return serverHistory;

  const existing = extractCrisisRowAfterUser(prev, userContent);
  let crisisRow: FiDisplayMessage | null = existing;
  if (!crisisRow && crisisFromCheck?.crisis_detected) {
    crisisRow = {
      role: 'assistant',
      content: '',
      fi_kind: 'crisis',
      crisis: {
        emergency: crisisFromCheck.emergency,
        selfharm: crisisFromCheck.selfharm,
        crisisResource: crisisFromCheck.crisis_resource ?? undefined,
      },
      timestamp: new Date().toISOString(),
    };
  }
  if (!crisisRow) return serverHistory;

  const merged = [...serverHistory.slice(0, uidx + 1), crisisRow];
  for (let i = uidx + 1; i < serverHistory.length; i++) {
    const m = serverHistory[i];
    if (m.role === 'assistant' && m.fi_kind !== 'crisis') {
      merged.push({ ...m, content: '', tool_results: undefined });
    } else {
      merged.push(m);
    }
  }
  return merged;
}

const ECHO_FALLBACK =
  "I'm having trouble forming a response right now. Please try rephrasing your question, or start a new chat.";

/** If the server echoed the user's message as assistant text, replace with a safe fallback. */
function sanitizeAssistantEcho(history: FiDisplayMessage[], userContent: string): FiDisplayMessage[] {
  const trimmed = userContent.trim();
  if (!trimmed) return history;
  return history.map((m) => {
    if (m.role !== 'assistant' || m.fi_kind === 'crisis') return m;
    const content = (m.content || '').trim();
    if (content && content === trimmed) {
      return { ...m, content: ECHO_FALLBACK };
    }
    return m;
  });
}

export type FiDisplayMessage = FiMessageRow & {
  fi_kind?: 'crisis';
  crisis?: FiCrisisPayload;
  crisis_dismissed?: boolean;
};

export type FiSearchProgress = Pick<FiSearchProgressPayload, 'message' | 'key'>;
export type FiSearchPathway = FiStarterPathway | null;

interface FiContextValue {
  panelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  minimizeForNavigation: () => void;
  messages: FiDisplayMessage[];
  threadId: number | null;
  isSending: boolean;
  fiSearchProgress: FiSearchProgress | null;
  sendMessage: (text: string) => Promise<void>;
  queueAction: (action: FiActionPayload) => void;
  resetSession: () => Promise<void>;
  introDismissed: boolean;
  dismissIntro: () => void;
  introVisible: boolean;
  setIntroVisible: (visible: boolean) => void;
  dismissCrisisMessage: (index: number) => void;
  onStoryEngagedFromFi: (contentId?: number) => void;
  inputHint: string;
  setInputHint: (hint: string) => void;
  inputDraft: string;
  setInputDraft: (draft: string) => void;
  applyStarterAction: (action: FiStarterAction) => void;
  clearInputStarter: () => void;
  searchPathway: FiSearchPathway;
  setSearchPathway: (pathway: FiSearchPathway) => void;
}

const FiContext = createContext<FiContextValue | null>(null);

export const useFi = (): FiContextValue => {
  const ctx = useContext(FiContext);
  if (!ctx) throw new Error('useFi must be used within FiProvider');
  return ctx;
};

export const FiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, authToken } = useAuth();
  const history = useHistory();
  const fiFeatureEnabled = useFiFeatureEnabled();
  const fiEnabled = !!user?.caller_role_id && !user?.is_partial && fiFeatureEnabled;

  // Visibility is owned by the AssistantWidgetContext coordinator. Fi's
  // ``panelOpen`` is the derived "widget is open AND the active context is
  // fi" — so flipping to a Peersona conversation hides Fi without resetting
  // any of its internal state.
  const { widgetOpen, isFiActive, openWidget, closeWidget } = useAssistantWidget();
  const panelOpen = widgetOpen && isFiActive;

  const [messages, setMessages] = useState<FiDisplayMessage[]>([]);
  const [threadId, setThreadId] = useState<number | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [introDismissed, setIntroDismissed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(INTRO_KEY) === '1',
  );
  const [introVisible, setIntroVisibleState] = useState(false);
  const [inputHint, setInputHint] = useState('');
  const [inputDraft, setInputDraft] = useState('');
  const [searchPathway, setSearchPathwayState] = useState<FiSearchPathway>(null);
  const [fiSearchProgress, setFiSearchProgress] = useState<FiSearchProgress | null>(null);
  const fiProgressQueueRef = useRef<FiSearchProgress[]>([]);
  const fiProgressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fiProgressLastShownAtRef = useRef<number>(0);
  const pendingActions = useRef<FiActionPayload[]>([]);
  const historyHydrated = useRef(false);
  const followUpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fiSendGeneration = useRef(0);
  const crisisResultForSend = useRef<CrisisCheckResponse | null>(null);

  const { data: historyData, isSuccess: historyReady } = useFiHistory(fiEnabled);
  const queryClient = useQueryClient();
  // Re-runs when ``markFreshAuthSession`` writes the flag (via sessionStorageChange),
  // so Fi still scrubs even if history hydrated before auth finished Keycloak init.
  const fiFreshSessionFlag = useSessionStorage(FI_FRESH_SESSION_KEY);

  useEffect(() => {
    if (!fiEnabled) {
      historyHydrated.current = false;
      setThreadId(null);
      setMessages([]);
      if (panelOpen) closeWidget();
      setIntroVisibleState(false);
      setFiSearchProgress(null);
    }
  }, [fiEnabled, panelOpen, closeWidget]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const MIN_STEP_MS = 3000;

    const clearProgressQueue = () => {
      fiProgressQueueRef.current = [];
      if (fiProgressTimerRef.current) {
        clearTimeout(fiProgressTimerRef.current);
        fiProgressTimerRef.current = null;
      }
      fiProgressLastShownAtRef.current = 0;
    };

    const pump = () => {
      fiProgressTimerRef.current = null;
      const next = fiProgressQueueRef.current.shift();
      if (!next) return;
      fiProgressLastShownAtRef.current = Date.now();
      setFiSearchProgress(next);
      if (fiProgressQueueRef.current.length) {
        fiProgressTimerRef.current = setTimeout(pump, MIN_STEP_MS);
      }
    };

    const onProgress = (e: Event) => {
      const ce = e as CustomEvent<FiSearchProgressPayload>;
      const d = ce.detail;
      if (d?.message) {
        const next: FiSearchProgress = { message: d.message, key: d.key };

        // New backend key = new run; show immediately and reset queue cadence.
        if (fiSearchProgress?.key && next.key && next.key !== fiSearchProgress.key) {
          clearProgressQueue();
          fiProgressLastShownAtRef.current = Date.now();
          setFiSearchProgress(next);
          return;
        }

        fiProgressQueueRef.current.push(next);
        const elapsed = Date.now() - fiProgressLastShownAtRef.current;
        const delay = fiProgressLastShownAtRef.current ? Math.max(0, MIN_STEP_MS - elapsed) : 0;
        if (!fiProgressTimerRef.current) {
          fiProgressTimerRef.current = setTimeout(pump, delay);
        }
      }
    };
    window.addEventListener(FI_SEARCH_PROGRESS_EVENT, onProgress);
    return () => {
      window.removeEventListener(FI_SEARCH_PROGRESS_EVENT, onProgress);
      clearProgressQueue();
    };
    // Intentionally omit fiSearchProgress from deps; we only use it for key change heuristic.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!fiEnabled || !authToken || fiFreshSessionFlag !== '1') return;
    if (!consumeFiFreshSessionFlag()) return;
    const c = historyData?.conversation;
    if (c?.id != null) {
      void postFiReset(c.id).catch(() => {
        /* best-effort — UI still starts blank even if the reset call fails */
      });
    }
    setThreadId(null);
    setMessages([]);
    historyHydrated.current = true;
  }, [fiEnabled, authToken, fiFreshSessionFlag, historyData]);

  /** Hydrate the chat history from the server when the component mounts. */
  useEffect(() => {
    if (!fiEnabled || !historyReady || historyHydrated.current) return;
    try {
      if (sessionStorage.getItem(FI_FRESH_SESSION_KEY) === '1') return;
    } catch {
      /* ignore */
    }
    historyHydrated.current = true;
    const c = historyData?.conversation;
    // KINDLY-1077: a fresh login should start the Fi panel clean. We mark the
    // existing thread completed on the server (preserving it in
    // ``fi_conversations`` for analytics) and render an empty panel — the
    // user's next message will create a brand-new thread.
    if (consumeFiFreshSessionFlag()) {
      if (c?.id != null) {
        void postFiReset(c.id).catch(() => {
          /* best-effort — UI still starts blank even if the reset call fails */
        });
      }
      setThreadId(null);
      setMessages([]);
      return;
    }
    if (c) {
      setThreadId(c.id);
      setMessages((c.messages || []) as FiDisplayMessage[]);
    } else {
      setThreadId(null);
      setMessages([]);
    }
  }, [fiEnabled, historyReady, historyData]);

  useEffect(() => {
    const unlisten = history.listen(() => {
      if (widgetOpen) closeWidget();
    });
    return unlisten;
  }, [history, widgetOpen, closeWidget]);

  const openPanel = useCallback(() => openWidget({ kind: 'fi' }), [openWidget]);
  const closePanel = useCallback(() => closeWidget(), [closeWidget]);
  const togglePanel = useCallback(() => {
    if (panelOpen) {
      closeWidget();
    } else {
      // Always open Fi from the Fi bubble. Stale Peersona context is cleared
      // by AssistantWidgetProvider when Peersona is unavailable (KINDLY-1137).
      openWidget({ kind: 'fi' });
    }
  }, [panelOpen, closeWidget, openWidget]);

  const minimizeForNavigation = useCallback(() => {
    if (widgetOpen) closeWidget();
  }, [widgetOpen, closeWidget]);

  const dismissIntro = useCallback(() => {
    localStorage.setItem(INTRO_KEY, '1');
    setIntroDismissed(true);
    setIntroVisibleState(false);
  }, []);

  const setSearchPathway = useCallback((pathway: FiSearchPathway) => {
    setSearchPathwayState(pathway);
  }, []);

  const applyStarterAction = useCallback((action: FiStarterAction) => {
    setSearchPathwayState(action.pathway);
    setInputHint(action.hint);
    setInputDraft(action.draft);
  }, []);

  const clearInputStarter = useCallback(() => {
    setInputHint('');
    setInputDraft('');
  }, []);

  const setIntroVisible = useCallback((visible: boolean) => {
    setIntroVisibleState(visible);
  }, []);

  const dismissCrisisMessage = useCallback((index: number) => {
    setMessages((prev) => prev.map((m, i) => (i === index ? { ...m, crisis_dismissed: true } : m)));
  }, []);

  const queueAction = useCallback((action: FiActionPayload) => {
    pendingActions.current.push(action);
  }, []);

  const flushPendingActions = useCallback(() => {
    const out = [...pendingActions.current];
    pendingActions.current = [];
    return out;
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !fiEnabled) return;

      clearInputStarter();
      // KINDLY-982: forward the 'everything' tile selection to the backend
      // verbatim instead of stripping it to `undefined`. The backend uses
      // pathway to distinguish an explicit "Show me everything" pick from a
      // free-typed first turn — without it, a topic-only message routes to
      // the clarify card (re-asking destination) instead of running the
      // multi-lane search the member already opted into.
      const pathway = searchPathway ?? undefined;
      setSearchPathwayState(null);
      const actions = flushPendingActions();
      const userRow: FiDisplayMessage = {
        role: 'user',
        content: trimmed,
        timestamp: new Date().toISOString(),
      };
      setMessages((m) => [...m, userRow]);

      const sendGen = ++fiSendGeneration.current;
      crisisResultForSend.current = null;

      // Crisis check runs in parallel with Fi — does not block the chat request.
      if (!isCrisisCheckInCooldown()) {
        void fetchCrisisCheck(trimmed)
          .then((crisis) => {
            if (sendGen !== fiSendGeneration.current) return;
            crisisResultForSend.current = crisis;
            if (!crisis.crisis_detected) return;
            setCrisisCheckCooldown();
            setMessages((prev) => insertCrisisAfterUser(prev, trimmed, crisis));
          })
          .catch(() => {});
      }

      setFiSearchProgress(null);
      setIsSending(true);
      try {
        const res = await postFiChat({
          message: trimmed,
          thread_id: threadId ?? undefined,
          actions: actions.length ? actions : undefined,
          pathway,
        });
        if (sendGen !== fiSendGeneration.current) return;
        setThreadId(res.thread_id);
        setMessages((prev) => {
          const merged = mergeFiHistoryPreservingCrisis(
            prev,
            res.message_history as FiDisplayMessage[],
            trimmed,
            crisisResultForSend.current,
          );
          return sanitizeAssistantEcho(merged, trimmed);
        });
      } catch (err: unknown) {
        if (sendGen !== fiSendGeneration.current) return;
        const isTimeout =
          err instanceof Error && (err.name === 'AbortError' || err.message.toLowerCase().includes('timeout'));
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content: isTimeout
              ? 'This is taking longer than expected. Please try again, or start a new chat if Fi seems stuck.'
              : "I'm having trouble connecting right now. Please try again in a moment.",
            timestamp: new Date().toISOString(),
          },
        ]);
      } finally {
        if (sendGen === fiSendGeneration.current) {
          setFiSearchProgress(null);
          fiProgressQueueRef.current = [];
          if (fiProgressTimerRef.current) {
            clearTimeout(fiProgressTimerRef.current);
            fiProgressTimerRef.current = null;
          }
          fiProgressLastShownAtRef.current = 0;
          setIsSending(false);
        }
      }
    },
    [fiEnabled, flushPendingActions, threadId, searchPathway, clearInputStarter],
  );

  const resetSession = useCallback(async () => {
    if (followUpTimer.current) {
      clearTimeout(followUpTimer.current);
      followUpTimer.current = null;
    }
    const actions = flushPendingActions();
    const tid = threadId;
    // Keep hydrated so the history effect cannot race and restore the old thread
    // while reset is in flight (KINDLY-1022).
    historyHydrated.current = true;
    fiSendGeneration.current += 1;
    setIsSending(false);
    setFiSearchProgress(null);
    setThreadId(null);
    setMessages([]);
    clearInputStarter();
    setSearchPathwayState(null);
    if (tid != null) {
      try {
        await postFiReset(tid, actions.length ? actions : undefined);
      } catch {
        /* ignore */
      }
    }
    // Drop cached history so a later mount does not revive the completed thread.
    await queryClient.invalidateQueries({ queryKey: fiKeys.history() });
  }, [flushPendingActions, threadId, clearInputStarter, queryClient]);

  const onStoryEngagedFromFi = useCallback((contentId?: number) => {
    if (contentId != null) {
      pendingActions.current.push({ type: 'viewed_story', content_id: contentId });
    }
    if (followUpTimer.current) clearTimeout(followUpTimer.current);
    followUpTimer.current = setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            'Would you like to talk with someone about this, or explore related resources? Tell me what feels right.',
          timestamp: new Date().toISOString(),
        },
      ]);
    }, 60_000);
  }, []);

  useEffect(() => {
    return () => {
      if (followUpTimer.current) clearTimeout(followUpTimer.current);
    };
  }, []);

  const value = useMemo<FiContextValue>(
    () => ({
      panelOpen,
      openPanel,
      closePanel,
      togglePanel,
      minimizeForNavigation,
      messages,
      threadId,
      isSending,
      fiSearchProgress,
      sendMessage,
      queueAction,
      resetSession,
      introDismissed,
      dismissIntro,
      introVisible,
      setIntroVisible,
      dismissCrisisMessage,
      onStoryEngagedFromFi,
      inputHint,
      setInputHint,
      inputDraft,
      setInputDraft,
      applyStarterAction,
      clearInputStarter,
      searchPathway,
      setSearchPathway,
    }),
    [
      panelOpen,
      openPanel,
      closePanel,
      togglePanel,
      minimizeForNavigation,
      messages,
      threadId,
      isSending,
      fiSearchProgress,
      sendMessage,
      queueAction,
      resetSession,
      introDismissed,
      dismissIntro,
      introVisible,
      setIntroVisible,
      dismissCrisisMessage,
      onStoryEngagedFromFi,
      inputHint,
      setInputHint,
      inputDraft,
      applyStarterAction,
      clearInputStarter,
      searchPathway,
      setSearchPathway,
    ],
  );

  return <FiContext.Provider value={value}>{children}</FiContext.Provider>;
};
